import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause } from "lucide-react";

const ASPECT = { "9:16": "aspect-[9/16]", "1:1": "aspect-square", "16:9": "aspect-video" };
const SPEED_MS = { lent: 3200, normal: 2200, rapide: 1400 };

// Animated Reels/TikTok-style montage built from generated images (Ken Burns + crossfade + subtitles)
export default function ReelPlayer({ images = [], caption = "", format = "9:16", speed = "normal", maxWidth = 300 }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);
  const interval = SPEED_MS[speed] || 2200;

  useEffect(() => {
    if (!playing || images.length === 0) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % images.length), interval);
    return () => clearInterval(timer.current);
  }, [playing, images.length, interval]);

  if (images.length === 0) return null;
  const kenBurns = idx % 2 === 0
    ? { initial: { scale: 1.15, x: 10 }, animate: { scale: 1, x: 0 } }
    : { initial: { scale: 1, x: -10 }, animate: { scale: 1.15, x: 0 } };

  const subtitle = Array.isArray(caption) ? caption[idx % caption.length] : caption;

  return (
    <div className="relative mx-auto rounded-2xl overflow-hidden border border-border bg-black" style={{ maxWidth }} data-testid="reel-player">
      <div className={`relative ${ASPECT[format] || ASPECT["9:16"]}`}>
        <AnimatePresence mode="sync">
          <motion.img
            key={idx}
            src={images[idx]}
            alt="frame"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, ...kenBurns.initial }}
            animate={{ opacity: 1, ...kenBurns.animate }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.6 }, scale: { duration: interval / 1000, ease: "linear" }, x: { duration: interval / 1000, ease: "linear" } }}
          />
        </AnimatePresence>

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

        {subtitle && (
          <motion.div key={`cap-${idx}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="absolute bottom-12 inset-x-0 px-4 text-center">
            <span className="inline-block px-3 py-1.5 rounded-lg bg-black/55 backdrop-blur text-white font-display font-bold text-base leading-snug" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}>
              {subtitle}
            </span>
          </motion.div>
        )}

        {/* progress bars */}
        <div className="absolute top-3 inset-x-3 flex gap-1">
          {images.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full bg-white/30 overflow-hidden">
              <div className="h-full bg-white transition-all" style={{ width: i < idx ? "100%" : i === idx ? "100%" : "0%", transitionDuration: i === idx ? `${interval}ms` : "0ms" }} />
            </div>
          ))}
        </div>

        <button onClick={() => setPlaying((p) => !p)} className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white" data-testid="reel-playpause">
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
      </div>
    </div>
  );
}
